import React, { createContext, useContext, useState, useEffect } from "react";

import { useRouter } from "next/router";
import { getProductListings } from "../core/api/productListings";
import { KIDS } from "../core/constants";
import { SORT_OPTIONS } from "../core/constants";

const INITIAL_SORT_KEY = SORT_OPTIONS[0].key;

// TODO
// Update state when we navigate back with the browser

// ?filters=colour%3A20%2C27%2C6%2C1%7Cbrand%3A1%2C8826%7Cattr_16%3ALong%20sleeved&sort=price&page=2

export const ListingsContext = createContext();
export const useListingsContext = () => useContext(ListingsContext);

const getListingsOptionsFromQuery = (query, availableFilters) => {
  const { filters, page, sort } = query;

  // Get each param from the querystring

  let requestedFiltersFromQuery = [];

  if (typeof filters !== "undefined") {
    // Parse the querystring value so we have applied filter values
    // grouped by type
    const filtersByType = filters.split("|").map((filter) => {
      const type = filter.split(":")[0];
      const appliedValues = filter
        .split(":")[1]
        .split(",")
        .map((value) => {
          return decodeURI(value);
        });
      return {
        type,
        appliedValues,
      };
    });

    filtersByType.forEach((filterType) => {
      // For each of these type groups, find the matching type group
      // from the available filters for this category
      const matchingType = availableFilters.find((availableFilter) => {
        return availableFilter.type === filterType.type;
      });

      if (typeof matchingType !== "undefined") {
        // If we find a match, create a list of applied options filtered by
        // the applied filter values we have in the querystring
        const appliedOptions = [];
        matchingType.options.forEach((availableOption) => {
          if (
            filterType.appliedValues.includes(String(availableOption.value))
          ) {
            // The GraphQL API expects string values
            appliedOptions.push({
              ...availableOption,
              label: String(availableOption.label),
              value: String(availableOption.value),
            });
          }
        });

        // If we have applied options for this type, add the filter
        // to requestedFiltersFromQuery
        if (appliedOptions.length > 0) {
          requestedFiltersFromQuery.push({
            type: filterType.type,
            options: appliedOptions,
          });
        }
      }
    });
  }

  return {
    requestedFilters: requestedFiltersFromQuery,
    requestedPage: typeof page === "undefined" ? "1" : page,
    requestedSortKey: typeof sort === "undefined" ? INITIAL_SORT_KEY : sort,
  };
};

function ListingsContextProvider({
  category,
  brandSlug,
  isLatestDrop,
  children,
}) {
  const router = useRouter();
  const [listingsState, setListingsState] = useState(null);
  const [products, setProducts] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [loadedFilters, setLoadedFilters] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    if (router.isReady) {
      const { requestedFilters, requestedSortKey } =
        getListingsOptionsFromQuery(router.query, category.availableFilters);
      setListingsState({
        activeFilters: requestedFilters,
        currentSortKey: requestedSortKey,
      });
      setLoadedFilters(JSON.stringify(router.query));
    }
  }, [router.isReady, router.query]);

  useEffect(() => {
    // this is a hack to prevent the page from loading twice
    if (loadedFilters !== null) {
      setLoadingProducts(true);
      getFilteredProducts();
    }
  }, [loadedFilters]);

  const getFilteredProducts = async () => {
    const { requestedFilters, requestedPage, requestedSortKey } =
      getListingsOptionsFromQuery(router.query, category.availableFilters);

    const data = await getProductListings(
      category.rootCategorySlug,
      category.categorySlug,
      brandSlug,
      isLatestDrop,
      requestedFilters,
      parseInt(requestedPage),
      requestedSortKey
    );
    setProducts(data.products);
    setPageInfo(data.pageInfo);
    setLoadingProducts(false);
  };

  const setFilter = (type, option) => {
    // Add/remove filters to querystring
    // Query needs to be filters=colour:red,blue|brand:joules
    const encodedType = encodeURI(type);
    const encodedValue = encodeURI(option.value);
    let query = {
      ...router.query,
    };
    if (query.filters) {
      // We have already applied some filters
      let filters = query.filters.split("|");
      const filtersOfThisType = filters.find((filter) => {
        return filter.startsWith(type);
      });
      let otherFilters = filters.filter((filter) => {
        return filter !== filtersOfThisType;
      });
      if (filtersOfThisType) {
        // We already have filters of this type, so check if we have this value
        let values = filtersOfThisType.split(":")[1].split(",");
        if (values.includes(encodedValue)) {
          // If yes then remove the value
          values = values.filter((value) => {
            return value !== encodedValue;
          });
        } else {
          // If no then add the value
          values = [...values, encodedValue];
        }
        if (values.length > 0) {
          // If we still have values for this filter, add it to the list
          // of filters we still want
          values.join(",");
          otherFilters = [
            ...otherFilters,
            `${encodedType}:${values.join(",")}`,
          ];
        }
        // If we've toggled a filter off then check this wasn't the last filter,
        // if it was remove the filters query
        if (otherFilters.length > 0) {
          query.filters = otherFilters.join("|");
        } else {
          delete query.filters;
        }
      } else {
        // We don't yet have filters of this type, so just add this new
        // filter onto the end of the existing filters
        query.filters = `${query.filters}|${encodedType}:${encodedValue}`;
      }
    } else {
      // We don't yet have any filters of any type, just add this new filter
      query["filters"] = `${encodedType}:${encodedValue}`;
    }

    // Reset pagination if we are changing the filters
    if (query.page) delete query.page;

    router.push({ query });
  };

  const clearFiltersOfType = (type) => {
    // Remove all filters matching this type
    let query = { ...router.query };

    if (query.filters) {
      const filtersNotOfType = query.filters.split("|").filter((filter) => {
        return !filter.startsWith(encodeURI(type));
      });
      if (filtersNotOfType.length > 0) {
        query.filters = filtersNotOfType.join("|");
      } else {
        // If this means there are no filters left, delete filters
        // from the query full stop
        delete query.filters;
      }
    }

    // Reset pagination if we are changing the filters
    if (query.page) delete query.page;

    router.push({ query });
  };

  const clearAllFilters = () => {
    // Remove all filters
    let query = { ...router.query };
    if (query.filters) delete query.filters;

    // Reset pagination if we are changing the filters
    if (query.page) delete query.page;

    router.push({ query });
  };

  const getPage = (pageNum) => {
    // Change page
    let query = { ...router.query };

    if (pageNum === 1) {
      // Rest sort key if returning to default order
      delete query.page;
    } else {
      query.page = pageNum;
    }

    router.push({
      query,
    });
  };

  const handleSort = (sortKey) => {
    // Change sorting order
    let query = { ...router.query };

    // Reset pagination if we are changing the ordering
    if (query.page) delete query.page;

    if (sortKey === INITIAL_SORT_KEY) {
      // Rest sort key if returning to default order
      delete query.sort;
    } else {
      query.sort = sortKey;
    }

    router.push({
      query,
    });
  };

  const key = `${JSON.stringify(listingsState)}-${JSON.stringify([
    category.rootCategorySlug,
    category.categorySlug,
    category.brandSlug,
  ])}`;

  return (
    <ListingsContext.Provider
      value={{
        products,
        pageInfo: pageInfo,
        availableFilters: category.availableFilters,
        activeFilters: listingsState ? listingsState.activeFilters : null,
        currentSortKey: listingsState ? listingsState.currentSortKey : null,
        loadingProducts,
        getPage,
        handleSort,
        setFilter,
        clearFiltersOfType,
        clearAllFilters,
        showGenderFilter: isLatestDrop || category.rootCategorySlug === KIDS,
        showCategoryFilter: isLatestDrop,
        isBrandContext: brandSlug !== null,
        showCategoryLinks: !isLatestDrop,
        key,
      }}
    >
      <div>{children}</div>
    </ListingsContext.Provider>
  );
}

export default ListingsContextProvider;
